import React, { FC, useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router";
import { Input } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { t } from "i18next";
import CustomButton from "ui/Button";
import { Loading } from "ui/Loading";
import { getNoteSignersApi, sendToSign, validateSignerApi } from "api/sign";
import { setError } from "store/error";
import { COLOR } from "utils/constant";
import { PageTypePath } from "utils/pages";
import styles from "styles.module.css";
import { ComboBoxDropdown } from "ui/dropDown";
import "./SignatureForm.css";

interface NoteSigner {
	username?: string | null;
	userNumber?: number | null;
}
interface NoteSignersWithTypes {
	vessel: NoteSigner[];
	buyer: NoteSigner[];
}

interface SignerValidationState {
	isValid: boolean;
	isChecked: boolean;
	showError: boolean;
}

interface SignatureState {
	vesselSigner: NoteSigner;
	buyerSigner: NoteSigner;
	vesselSignerValid: SignerValidationState;
	buyerSignerValid: SignerValidationState;
	apprenticeSigner: string;
	quotaTypeCode: string;
	showError: boolean;
}

const createEmptyNoteSigner = (): NoteSigner => ({
	username: null,
	userNumber: null,
});

const createValidationState = (): SignerValidationState => ({
	isValid: false,
	isChecked: false,
	showError: false,
});

const createInitialState = (): SignatureState => ({
	vesselSigner: createEmptyNoteSigner(),
	buyerSigner: createEmptyNoteSigner(),
	vesselSignerValid: createValidationState(),
	buyerSignerValid: createValidationState(),
	apprenticeSigner: "",
	quotaTypeCode: "",
	showError: false,
});

const normalizeNoteSigner = (value: NoteSigner | string | null): NoteSigner => {
	if (!value) {
		return createEmptyNoteSigner();
	}
	if (typeof value === "string") {
		return {
			username: value.toUpperCase(),
			userNumber: Date.now(),
		};
	}
	return {
		...value,
		username: value.username?.toUpperCase() || "",
		userNumber: value.userNumber || Date.now(),
	};
};

export const SignatureForm: FC = () => {
	const [signers, setSigners] = useState<SignatureState>(createInitialState());
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { id } = useParams();
	const location = useLocation();
	const navigate = useNavigate();
	const [noteSigners, setNoteSigners] = useState<NoteSignersWithTypes>({
		vessel: [],
		buyer: [],
	});

	const navigationState = location.state as {
		quotaTypeCode: string;
		noteType: number;
	} | null;

	useEffect(() => {
		if (!id) return;

		setSigners((prev) => ({
			...prev,
			quotaTypeCode: navigationState?.quotaTypeCode as any,
		}));

		getNoteSigners(id, 0); // 0 for vessel signer
		getNoteSigners(id, 1); // 1 for buyer signer
	}, [id, navigationState?.quotaTypeCode]);

	const isValid = (fieldValue?: string | null) => {
		if (!fieldValue) {
			return false;
		}

		const normalizedValue = fieldValue.trim().toUpperCase();
		const signaturePattern = /^\d{6}[A-Z]{6}$/;
		return signaturePattern.test(normalizedValue);
	};

	const sendSigners = () => {
		const vesselUsername = signers.vesselSigner.username;
		const buyerUsername = signers.buyerSigner.username;
		const vesselValidState = signers.vesselSignerValid;
		const buyerValidState = signers.buyerSignerValid;

		if (
			!vesselValidState.isChecked ||
			!vesselValidState.isValid ||
			!isValid(vesselUsername)
		) {
			setSigners((prev) => ({
				...prev,
				vesselSignerValid: {
					...prev.vesselSignerValid,
					showError: true,
				},
			}));
			return;
		}
		if (
			!buyerValidState.isChecked ||
			!buyerValidState.isValid ||
			!isValid(buyerUsername)
		) {
			setSigners((prev) => ({
				...prev,
				buyerSignerValid: {
					...prev.buyerSignerValid,
					showError: true,
				},
			}));
			return;
		}

		if (signers.quotaTypeCode === "14" && !isValid(signers?.apprenticeSigner)) {
			setSigners((prev) => ({ ...prev, showError: true }));
			return;
		}

		setIsSubmitting(true);
		sendToSign({
			vesselSigner: vesselUsername,
			buyerSigner: buyerUsername,
			apprenticeSigner: signers.apprenticeSigner,
			documentNumber: id as any,
		})
			.then((data) => {
				if (navigationState?.noteType === 0) {
					navigate(`/${PageTypePath.SalesNotesList}`);
					return;
				}

				navigate(`/${PageTypePath.LandingNoteList}`);
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			})
			.finally(() => {
				setIsSubmitting(false);
			});
	};

	const validateSigner = (signer: string, type: 0 | 1) => {
		if (!isValid(signer)) return;

		validateSignerApi(signer)
			.then((data) => {
				setSigners((prev) => {
					const validationState: SignerValidationState = {
						isValid: data.isValid,
						isChecked: true,
						showError: true,
					};

					if (type === 0) {
						return {
							...prev,
							vesselSigner: {
								...prev.vesselSigner,
								username: signer,
							},
							vesselSignerValid: validationState,
						};
					}

					return {
						...prev,
						buyerSigner: {
							...prev.buyerSigner,
							username: signer,
						},
						buyerSignerValid: validationState,
					};
				});
			})
			.catch((err) => {
				console.log(err);
			});
	};

	const getNoteSigners = (noteNote: string, signerType: 0 | 1) => {
		getNoteSignersApi(noteNote, signerType)
			.then((data) => {
				if (signerType === 0) {
					setNoteSigners((prev) => ({ ...prev, vessel: data }));
				} else {
					setNoteSigners((prev) => ({ ...prev, buyer: data }));
				}
			})
			.catch((err) => {
				console.log(err);
			});
	};

	const renderValidationStatus = (validationState: SignerValidationState) => {
		if (!validationState?.isChecked) {
			return null;
		}

		const color = validationState.isValid ? COLOR.StatusGreen : COLOR.StatusRed;
		const message = validationState.isValid
			? t("SignerValidated", { defaultValue: "Signer verified" })
			: t("SignerInvalid", { defaultValue: "Signer could not be verified" });
		const iconSrc = validationState.isValid
			? "/icon/checkmark_green.svg"
			: "/icon/close.svg";

		return (
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: 8,
					marginTop: 8,
					color,
				}}
			>
				<img src={iconSrc} alt={message} width={20} height={20} />
				<span className={styles.TableText} style={{ color }}>
					{message}
				</span>
			</div>
		);
	};

	return (
		<div className={styles.PageContainer} style={{ height: "80vh" }}>
			<Loading show={isSubmitting} />
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>
					{t("Send") +
						" " +
						t("note").toLocaleLowerCase() +
						" " +
						`${id}` +
						" " +
						t("to") +
						" " +
						t("signing")}
				</legend>
			</div>
			<div className="signature-page-input-grid">
				<div className="k-form-field-wrap signature-page-input-field">
					<Label>{t("FishermanSignatory")}*</Label>
					{/* <Input
						placeholder={t("SignatoryPlaceholder")}
						width={"100%"}
						value={signers?.vesselSigner}
						valid={!(signers?.showError && !isValid(signers.vesselSigner))}
						onChange={(e) =>
							setSigners({
								...signers,
								vesselSigner: (e.value as string)?.toUpperCase(),
							})
						}
					/> */}
					<ComboBoxDropdown
						placeholder={t("SignatoryPlaceholder")}
						value={signers.vesselSigner}
						onChange={(e) => {
							const nextSigner = normalizeNoteSigner(
								e.value as NoteSigner | string | null,
							);
							setSigners((prev) => ({
								...prev,
								vesselSigner: nextSigner,
								vesselSignerValid: {
									...prev.vesselSignerValid,
									isChecked: false,
									showError: true,
								},
							}));
							if (nextSigner.username) {
								validateSigner(nextSigner.username, 0);
							}
						}}
						dataItemKey="userNumber"
						textField="username"
						valid={
							!(
								signers.vesselSignerValid.showError &&
								!isValid(signers.vesselSigner.username)
							)
						}
						data={noteSigners.vessel}
					/>
					{renderValidationStatus(signers.vesselSignerValid)}
				</div>
				{signers?.quotaTypeCode === "14" && (
					<div className="k-form-field-wrap signature-page-input-field">
						<Label>{t("ApprenticeSignatory")}*</Label>
						<Input
							placeholder={t("SignatoryPlaceholder")}
							width={"100%"}
							value={signers?.apprenticeSigner}
							valid={
								!(
									signers?.showError &&
									!isValid(signers.apprenticeSigner) &&
									signers.quotaTypeCode === "14"
								)
							}
							onChange={(e) =>
								setSigners({
									...signers,
									apprenticeSigner: (e.value as string)?.toUpperCase(),
								})
							}
						/>
					</div>
				)}

				<div className="k-form-field-wrap signature-page-input-field">
					<Label>{t("BuyerSignatory")}*</Label>
					{/* <Input
						placeholder={t("SignatoryPlaceholder")}
						width={"100%"}
						value={signers?.buyerSigner}
						valid={!(signers?.showError && !isValid(signers.buyerSigner))}
						onChange={(e) =>
							setSigners({
								...signers,
								buyerSigner: (e.value as string)?.toUpperCase(),
							})
						}
					/> */}
					<ComboBoxDropdown
						placeholder={t("SignatoryPlaceholder")}
						value={signers.buyerSigner}
						onChange={(e) => {
							const nextSigner = normalizeNoteSigner(
								e.value as NoteSigner | string | null,
							);
							setSigners((prev) => ({
								...prev,
								buyerSigner: nextSigner,
								buyerSignerValid: {
									...prev.buyerSignerValid,
									isChecked: false,
									showError: true,
								},
							}));
							if (nextSigner.username) {
								validateSigner(nextSigner.username, 1);
							}
						}}
						dataItemKey="userNumber"
						textField="username"
						valid={
							!(
								signers.buyerSignerValid.showError &&
								!isValid(signers.buyerSigner.username)
							)
						}
						data={noteSigners.buyer}
					/>
					{renderValidationStatus(signers.buyerSignerValid)}
				</div>
			</div>
			<div className="signature-page-info-block">
				<span className="signature-page-info-title">{t("SignatoryText")}</span>
				<div>
					<span>{t("SignatoryDate")}</span> <br />
					<span>{t("SignatoryLastname")}</span> <br />
					<span>{t("SignatoryFirstname")}</span>
				</div>
			</div>

			<div
				style={{
					width: "100%",
					marginTop: "30px",
					display: "flex",
					justifyContent: "center",
				}}
			>
				<CustomButton
					onClick={sendSigners}
					label={isSubmitting ? t("Sending") + "..." : t("Send")}
					bgColor={COLOR.Green}
					color={"white"}
					disabled={isSubmitting}
					// iconName={ICON_NAMES.FinishTransparent}
				/>
			</div>
		</div>
	);
};

export default SignatureForm;
